/**
 * The two things a key can do.
 *
 * A panel announces a press and a release, and the technician maps each to its
 * own set of load states. The numbers are the reference app's (`services.
 * pressType`: press 1, release 0) and the only values its validator accepts.
 *
 * The firmware's exporter also knows `hold` (2) and `double tap` (4). They are
 * left out on purpose: nothing in the reference's own configuration accepts
 * them, so a technician could program a state that never fires.
 */
export const KEY_STATE = {
  PRESS: 'press',
  RELEASE: 'release',
} as const;

export type KeyState = (typeof KEY_STATE)[keyof typeof KEY_STATE];

/** What the firmware's trigger vector carries for each state. */
export const KEY_STATE_WIRE = {
  [KEY_STATE.PRESS]: 1,
  [KEY_STATE.RELEASE]: 0,
} as const satisfies Record<KeyState, number>;

export interface KeyStateSpec {
  readonly label: string;
  /** Short form for the panel canvas and summaries. */
  readonly shortLabel: string;
  readonly wireValue: number;
  readonly hint: string;
}

export const KEY_STATE_SPECS = {
  [KEY_STATE.PRESS]: {
    label: 'Press',
    shortLabel: 'On',
    wireValue: KEY_STATE_WIRE[KEY_STATE.PRESS],
    hint: 'Fires as the key goes down.',
  },
  [KEY_STATE.RELEASE]: {
    label: 'Release',
    shortLabel: 'Off',
    wireValue: KEY_STATE_WIRE[KEY_STATE.RELEASE],
    hint: 'Fires as the key comes back up — on panels that report a release, this is the "off" half of the button.',
  },
} as const satisfies Record<KeyState, KeyStateSpec>;

/** Both states, in the order the console shows them. */
export const KEY_STATES = [KEY_STATE.PRESS, KEY_STATE.RELEASE] as const satisfies readonly KeyState[];

/** The state a press of a given set of states pairs with. */
export const oppositeState = (state: KeyState): KeyState =>
  state === KEY_STATE.PRESS ? KEY_STATE.RELEASE : KEY_STATE.PRESS;
